import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  Checkbox,
  Divider,
  Group,
  Modal,
  Paper,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { AlertCircle, AlertTriangle as AlertTriangleIcon, Eye, Mail, Star, Tag } from 'react-feather';
import {
  RichTextEditor,
  type RichTextEditorHandle,
  type RichTextControl,
} from '../../../../components/common/RichTextEditor';
import { useInsertAtCursor } from '../../../../hooks';
import { coachPostSessionApi, type PostSessionParticipant } from '../../../../services/api/coachPostSession';
import { DEFAULT_RECAP_SUBJECT } from './recapEmailTemplate';

/**
 * MED-901 / E2.16 — "Send Recap Email" modal.
 *
 * Built on the `SendReminderModal` pattern (cohorts/components/SendReminderModal.tsx)
 * with these differences:
 *   • Body is a RichTextEditor (AC8) — not a Textarea. Merge-tag insertion at
 *     cursor uses the editor's imperative `insertText` handle (added in this
 *     story); Subject uses the existing `useInsertAtCursor` hook.
 *   • Recipients come from the parent (the post-session bundle has them
 *     already) — no separate fetch.
 *   • Merge tag set is the recap-email set (AC6) — not the intake set.
 *   • Default subject pre-fills `Session {{session_number}} Recap — {{cohort_name}}`
 *     (AC7) but only when the parent hasn't passed a prior draft.
 *   • Preview toggle (AC9) resolves the seven tags client-side for display;
 *     the authoritative resolution happens server-side at send time.
 *   • Confirmation sub-modal "Confirm & Send" (AC10) before the actual send.
 *   • Success / failure notification after send (AC11).
 */

/** Merge tags available for recap email (AC6). Definition is local — the
 *  authoritative tag set lives on the server (AC15) but the chips and the
 *  preview resolver mirror it here. */
const MERGE_TAGS: Array<{ tag: string; label: string; sample: string }> = [
  { tag: '{{first_name}}', label: 'First name', sample: 'Sam' },
  { tag: '{{cohort_name}}', label: 'Cohort name', sample: 'Q2 Leadership' },
  { tag: '{{session_number}}', label: 'Session #', sample: '3' },
  { tag: '{{session_date}}', label: 'Session date', sample: 'May 15, 2026' },
  { tag: '{{group_name}}', label: 'Group name', sample: 'Group A' },
  { tag: '{{topic_name}}', label: 'Topic', sample: 'Difficult conversations' },
  { tag: '{{coach_name}}', label: 'Coach name', sample: 'Jamie Coach' },
  // MED-971 AC2 — new tag added to the recap-email set, mirrored in
  // utils/recapEmailMergeTags.ts on the server. Empty string when the cohort
  // has no next session scheduled.
  { tag: '{{next_session_date}}', label: 'Next session date', sample: 'May 22, 2026' },
];

/**
 * MED-1148 — reduced formatting bar for the recap email body, mirroring the
 * MED-1014 notes toolbar (participant ReviewNotesCard / coach SessionNotesCard).
 * Drops headings, strikethrough, horizontal rule, and blockquote from the full
 * DEFAULT_TOOLBAR — leaving inline emphasis, lists, and links — for both the
 * coach and peer leader post-session experiences (this modal is shared by both).
 */
const RECAP_EMAIL_TOOLBAR: RichTextControl[][] = [
  ['bold', 'italic', 'underline'],
  ['bulletList', 'orderedList'],
  ['link'],
];

const SESSION_DATE_DISPLAY_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
};

function formatSessionDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', SESSION_DATE_DISPLAY_FORMAT_OPTIONS);
}

/** Replace merge tags in a string using a per-recipient context. Mirrors the
 *  server resolver; null → empty string. Used only for the preview. */
function resolveTagsForPreview(
  input: string,
  ctx: {
    firstName: string;
    cohortName: string;
    sessionNumber: string;
    sessionDate: string;
    groupName: string;
    topicName: string;
    coachName: string;
    nextSessionDate: string;
  },
): string {
  const map: Record<string, string> = {
    '{{first_name}}': ctx.firstName,
    '{{cohort_name}}': ctx.cohortName,
    '{{session_number}}': ctx.sessionNumber,
    '{{session_date}}': ctx.sessionDate,
    '{{group_name}}': ctx.groupName,
    '{{topic_name}}': ctx.topicName,
    '{{coach_name}}': ctx.coachName,
    '{{next_session_date}}': ctx.nextSessionDate,
  };
  let output = input;
  for (const [tag, value] of Object.entries(map)) {
    output = output.split(tag).join(value);
  }
  return output;
}

export interface SendRecapEmailModalProps {
  opened: boolean;
  onClose: () => void;
  /** Persisted across opens — the parent saves the latest subject after close (AC3). */
  onDraftChange: (next: { subject: string; bodyHtml: string }) => void;
  /** Resume state from sessionStorage. Empty strings on first open. */
  initialSubject: string;
  initialBodyHtml: string;
  /** MED-893 AC8 — when true, the body content was pre-populated from the AI
   *  recap-draft response and the "AI-drafted · review before sending" pill is
   *  shown near the body field. Falls back to false (no pill) when the modal is
   *  resuming a coach's saved draft. */
  aiDrafted?: boolean;
  /** Session context for merge tags + the send endpoint. */
  sessionId: string;
  participants: PostSessionParticipant[];
  cohortName: string;
  groupName: string;
  sessionNumber: number | null;
  /** ISO 8601 — formatted "MMM d, yyyy" for the preview, raw value sent to API. */
  sessionDateIso: string | null;
  topicName: string;
  coachName: string;
  /**
   * MED-971 AC2 — ISO 8601 scheduled_start of the next group session for the
   * cohort. Used for the `{{next_session_date}}` preview tag. Null when no
   * future session is scheduled — tag resolves to empty string.
   */
  nextSessionDateIso: string | null;
  /**
   * MED-909 — optional send-function injection. When provided, replaces the
   * default `coachPostSessionApi.sendRecapEmail` call so the same modal can
   * serve the peer leader post-session path (`peerLeaderPostSessionApi.sendRecapEmail`).
   * The signature matches the coach API exactly: returns `{ sent, failed, skipped_not_in_session }`.
   */
  sendFn?: (
    sessionId: string,
    body: { recipient_user_ids: string[]; subject: string; body_html: string },
  ) => Promise<{ sent: number; failed: number; skipped_not_in_session: number }>;
}

export function SendRecapEmailModal({
  opened,
  onClose,
  onDraftChange,
  initialSubject,
  initialBodyHtml,
  aiDrafted = false,
  sessionId,
  participants,
  cohortName,
  groupName,
  sessionNumber,
  sessionDateIso,
  topicName,
  coachName,
  nextSessionDateIso,
  sendFn,
}: SendRecapEmailModalProps) {
  // MED-1252 — the default subject now lives in recapEmailTemplate.ts so the
  // read-only card can seed the same value. The editor still renders it raw
  // (editable merge-tag tokens) — resolution stays in the Preview pane / server.
  const [subject, setSubject] = useState<string>(initialSubject || DEFAULT_RECAP_SUBJECT);
  const [bodyHtml, setBodyHtml] = useState<string>(initialBodyHtml);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    () => new Set(participants.map(p => p.user_id)),
  );
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const [lastFocusedField, setLastFocusedField] = useState<'subject' | 'body'>('body');
  const [confirmOpened, setConfirmOpened] = useState<boolean>(false);
  const [sending, setSending] = useState<boolean>(false);

  const subjectField = useInsertAtCursor<HTMLInputElement>();
  const bodyEditorRef = useRef<RichTextEditorHandle>(null);

  // MED-893 AC8 — pill is the parent's `aiDrafted` AND the modal's body still
  // byte-matches what was seeded on open. Body only (2026-06-11): the subject
  // is no longer an AI contribution — the E2.16 default fills it on open — so
  // subject state must neither show nor hide the pill. Once the coach edits
  // the body, the pill hides for the rest of the open session — and it also
  // stays hidden if the AI fetch resolved AFTER the modal was opened (the
  // modal would NOT have re-seeded, so internal != initial).
  const showAiPill = aiDrafted && bodyHtml === initialBodyHtml;

  // Re-seed state on open so resume-from-sessionStorage works correctly.
  useEffect(() => {
    if (opened) {
      setSubject(initialSubject || DEFAULT_RECAP_SUBJECT);
      setBodyHtml(initialBodyHtml);
      setSelectedIds(new Set(participants.map(p => p.user_id)));
      setShowPreview(false);
    }
    // We deliberately only re-seed when `opened` flips — interior edits should
    // persist within a single open session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  const selectedCount = selectedIds.size;
  const allSelected = selectedCount === participants.length && participants.length > 0;

  const toggleParticipant = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(participants.map(p => p.user_id)));
  };

  const handleInsertTag = (tag: string) => {
    if (lastFocusedField === 'subject') {
      subjectField.insert(tag, () => subject, setSubject);
    } else {
      bodyEditorRef.current?.insertText(tag);
    }
  };

  const persistDraft = useCallback(
    (nextSubject: string, nextBodyHtml: string) => {
      onDraftChange({ subject: nextSubject, bodyHtml: nextBodyHtml });
    },
    [onDraftChange],
  );

  // Build the preview using actual session context where available, sample
  // fallback for the per-recipient `first_name` (AC9).
  const previewContext = {
    firstName: 'Sam',
    cohortName: cohortName || 'Sample Cohort',
    sessionNumber: sessionNumber != null ? String(sessionNumber) : '',
    sessionDate: formatSessionDate(sessionDateIso),
    groupName: groupName || 'Sample Group',
    topicName: topicName || '',
    coachName: coachName || 'Coach',
    nextSessionDate: formatSessionDate(nextSessionDateIso),
  };
  const previewSubject = resolveTagsForPreview(subject, previewContext);
  const previewBodyHtml = resolveTagsForPreview(bodyHtml, previewContext);

  const handleClose = () => {
    // Save before closing so the read-only preview card reflects the latest
    // draft (AC3). The parent owns sessionStorage.
    persistDraft(subject, bodyHtml);
    onClose();
  };

  const handleSend = async () => {
    if (selectedCount === 0) return;
    if (!subject.trim() || !bodyHtml.trim()) {
      notifications.show({
        title: 'Cannot send empty email',
        message: 'Subject and body are both required.',
        color: 'red',
      });
      return;
    }

    setSending(true);
    try {
      const send = sendFn ?? coachPostSessionApi.sendRecapEmail;
      const result = await send(sessionId, {
        recipient_user_ids: Array.from(selectedIds),
        subject,
        body_html: bodyHtml,
      });
      const totalAttempted = result.sent + result.failed;
      if (result.sent > 0 && result.failed === 0) {
        notifications.show({
          title: 'Recap email sent',
          message: `Sent to ${result.sent} participant${result.sent === 1 ? '' : 's'}.`,
          color: 'medleyGreen',
        });
      } else if (result.sent > 0 && result.failed > 0) {
        notifications.show({
          title: 'Partial send',
          message: `Sent to ${result.sent} of ${totalAttempted}; ${result.failed} failed.`,
          color: 'warningAmber',
        });
      } else {
        notifications.show({
          title: 'Send failed',
          message: 'No emails were sent. Please try again.',
          color: 'red',
        });
      }
      persistDraft(subject, bodyHtml);
      onClose();
    } catch (err) {
      notifications.show({
        title: 'Send failed',
        message: err instanceof Error ? err.message : 'Could not send recap email.',
        color: 'red',
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Group gap="xs">
          <Mail size={18} />
          <Text fw={600}>Send Recap Email</Text>
        </Group>
      }
      size="lg"
      centered
    >
      <Stack gap="md">
        <Alert color="blue" icon={<AlertCircle size={16} />} variant="light">
          <Text size="sm">
            Compose a recap of the session for your participants. Merge tags are resolved
            individually for each recipient before sending.
          </Text>
        </Alert>

        {/* Recipients */}
        <Box>
          <Group justify="space-between" mb={8}>
            <Text size="sm" fw={500}>
              Select Recipients
            </Text>
            <UnstyledButton onClick={toggleAll}>
              <Text size="xs" c="blue" fw={500}>
                {allSelected ? 'Deselect All' : 'All Participants'}
              </Text>
            </UnstyledButton>
          </Group>
          <Paper withBorder p={0}>
            <ScrollArea.Autosize mah={220}>
              <Stack gap={0}>
                {participants.map(recipient => (
                  <UnstyledButton
                    key={recipient.user_id}
                    onClick={() => toggleParticipant(recipient.user_id)}
                    px="sm"
                    py={8}
                    style={theme => ({
                      borderBottom: `1px solid ${theme.colors.gray[2]}`,
                    })}
                  >
                    <Group gap="sm" wrap="nowrap">
                      <Checkbox
                        checked={selectedIds.has(recipient.user_id)}
                        onChange={() => {}}
                        tabIndex={-1}
                        styles={{ input: { cursor: 'pointer', pointerEvents: 'none' } }}
                      />
                      <Box style={{ flex: 1, minWidth: 0 }}>
                        <Group gap="xs" wrap="nowrap">
                          <Text size="sm" truncate>
                            {recipient.first_name} {recipient.last_name}
                          </Text>
                          <Text size="xs" c="dimmed" truncate>
                            {recipient.email}
                          </Text>
                        </Group>
                      </Box>
                    </Group>
                  </UnstyledButton>
                ))}
              </Stack>
            </ScrollArea.Autosize>
            <Box
              px="sm"
              py={6}
              style={theme => ({
                borderTop: `1px solid ${theme.colors.gray[2]}`,
                backgroundColor: theme.colors.gray[0],
              })}
            >
              <Text size="xs" c="dimmed">
                {selectedCount} of {participants.length} participant
                {participants.length !== 1 ? 's' : ''} selected
              </Text>
            </Box>
          </Paper>
        </Box>

        {/* Merge tag chips */}
        <Box>
          <Group gap="xs" mb={8}>
            <Tag size={14} />
            <Text size="sm" fw={500}>
              Insert Merge Tag
            </Text>
          </Group>
          <Group gap="xs">
            {MERGE_TAGS.map(({ tag, label }) => (
              <Tooltip key={tag} label={`Insert ${tag}`}>
                <Badge
                  variant="light"
                  color="gray"
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleInsertTag(tag)}
                >
                  {label}
                </Badge>
              </Tooltip>
            ))}
          </Group>
        </Box>

        <TextInput
          ref={subjectField.ref}
          label="Subject"
          placeholder="Session recap"
          value={subject}
          onChange={e => {
            const next = e.currentTarget.value;
            setSubject(next);
          }}
          onFocus={() => setLastFocusedField('subject')}
          onSelect={subjectField.trackCursor}
        />

        <Box>
          <Group justify="space-between" align="center" mb={6}>
            <Text size="sm" fw={500}>
              Body
            </Text>
            {showAiPill && (
              <Badge
                size="sm"
                variant="light"
                leftSection={<Star size={10} />}
                styles={{
                  root: {
                    backgroundColor: '#f3f0ff',
                    color: '#5a3eb6',
                    textTransform: 'none',
                    fontWeight: 500,
                  },
                }}
              >
                AI-drafted &middot; review before sending
              </Badge>
            )}
          </Group>
          <RichTextEditor
            ref={bodyEditorRef}
            value={bodyHtml}
            onChange={setBodyHtml}
            toolbar={RECAP_EMAIL_TOOLBAR}
            placeholder="Compose a recap of the session for participants…"
            minHeight={220}
            onFocus={() => setLastFocusedField('body')}
          />
        </Box>

        {/* Preview toggle */}
        <Group justify="flex-end">
          <Button
            variant="subtle"
            size="xs"
            leftSection={<Eye size={14} />}
            onClick={() => setShowPreview(s => !s)}
          >
            {showPreview ? 'Hide Preview' : 'Show Preview'}
          </Button>
        </Group>

        {showPreview && (
          <Paper withBorder p="md" bg="gray.0">
            <Stack gap="xs">
              <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
                Preview (sample {`{{first_name}}`})
              </Text>
              <Divider />
              <Box>
                <Text size="sm" fw={500}>
                  Subject:
                </Text>
                <Text size="sm">{previewSubject || <Text component="span" c="dimmed" fs="italic">No subject</Text>}</Text>
              </Box>
              <Box>
                <Text size="sm" fw={500}>
                  Body:
                </Text>
                {previewBodyHtml.trim() ? (
                  <Box
                    p="sm"
                    style={{ borderRadius: 6, background: 'white' }}
                    dangerouslySetInnerHTML={{ __html: previewBodyHtml }}
                  />
                ) : (
                  <Text size="sm" c="dimmed" fs="italic">
                    No body content
                  </Text>
                )}
              </Box>
            </Stack>
          </Paper>
        )}

        <Divider />

        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={handleClose} disabled={sending}>
            Cancel
          </Button>
          <Button
            onClick={() => setConfirmOpened(true)}
            disabled={selectedCount === 0 || !subject.trim() || !bodyHtml.trim() || sending}
            loading={sending}
            color="dark"
          >
            Send to {selectedCount} Participant{selectedCount !== 1 ? 's' : ''}
          </Button>
        </Group>
      </Stack>

      {/* Confirmation sub-modal — same z-index strategy as SendReminderModal */}
      <Modal
        opened={confirmOpened}
        onClose={() => setConfirmOpened(false)}
        title="Confirm Email Send"
        size="sm"
        centered
        zIndex={301}
      >
        <Stack gap="md">
          <Alert icon={<AlertTriangleIcon size={16} />} color="yellow" variant="light">
            <Text size="sm">
              You are about to send this recap email to{' '}
              <Text component="span" fw={700}>
                {selectedCount} participant{selectedCount !== 1 ? 's' : ''}
              </Text>
              . This action cannot be undone.
            </Text>
          </Alert>
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={() => setConfirmOpened(false)} disabled={sending}>
              Cancel
            </Button>
            <Button
              color="medleyGreen"
              onClick={() => {
                setConfirmOpened(false);
                void handleSend();
              }}
              loading={sending}
              disabled={sending}
            >
              Confirm &amp; Send
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Modal>
  );
}

export default SendRecapEmailModal;
